import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'wouter';
import { api, type ListVendorsResponse, type Vendor } from '@/lib/api';
import { phoneCountry, formatPhoneDisplay } from '@/lib/phone-country';

interface Props {
  selectedVendorId: string | null;
}

export function VendorList({ selectedVendorId }: Props) {
  const { getToken } = useAuth();
  const [q, setQ] = useState('');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['vendors', { q }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      const qs = params.toString();
      return api<ListVendorsResponse>(`/api/vendors${qs ? '?' + qs : ''}`, {
        tokenGetter: () => getToken(),
      });
    },
    staleTime: 10_000,
  });

  return (
    <div className="h-full flex flex-col">
      <div className="p-3 border-b border-border bg-background flex-shrink-0">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter vendors…" title="Filter by company, contact name, email, phone, PN…"
          className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
        />
      </div>

      {isLoading && <div className="p-4 text-sm text-muted-foreground">Loading…</div>}
      {isError && (
        <div className="p-4 text-sm text-destructive">
          {(error as { message?: string })?.message ?? 'Failed to load vendors'}
        </div>
      )}

      <ul className="flex-1 overflow-auto">
        {(data?.vendors ?? []).map((v) => (
          <VendorRow key={v.id} v={v} selected={v.id === selectedVendorId} />
        ))}
        {data && data.vendors.length === 0 && (
          <li className="p-4 text-sm italic text-muted-foreground">
            {q ? 'No matches.' : 'No vendors yet. They populate as quotes come in.'}
          </li>
        )}
      </ul>
    </div>
  );
}

function VendorRow({ v, selected }: { v: Vendor; selected: boolean }) {
  // Derive country/flag from contact phone country code (e.g. +971 → 🇦🇪 AE).
  const phoneInfo = phoneCountry(v.contactPhone);
  const hasContactInfo = v.contactFullName || v.contactPhone || v.contactEmail;
  return (
    <li>
      <Link
        href={`/vendors/${v.id}`}
        className={`block px-4 py-3 border-b border-border hover:bg-muted/40 ${
          selected ? 'bg-muted/60' : ''
        }`}
      >
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <div className="font-semibold text-sm">
            {v.displayName ?? v.legalName}
            {v.aogCapable && (
              <span className="ml-2 px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded bg-amber-100 text-amber-900">
                AOG
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {v.totalQuotesReceived} quote{v.totalQuotesReceived === 1 ? '' : 's'}
          </div>
        </div>
        {hasContactInfo && (
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
            {v.contactFullName && <span className="font-medium">{v.contactFullName}</span>}
            {v.contactPhone && (
              <span className="font-mono text-muted-foreground">
                {formatPhoneDisplay(v.contactPhone)}
              </span>
            )}
            {!v.contactFullName && !v.contactPhone && v.contactEmail && (
              <span className="text-muted-foreground">{v.contactEmail}</span>
            )}
            {phoneInfo && (
              <span
                className="text-muted-foreground"
                title={phoneInfo.name}
                aria-label={phoneInfo.name}
              >
                {phoneInfo.flag} {phoneInfo.iso2}
              </span>
            )}
          </div>
        )}
        <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
          <span>win {v.winRate}%</span>
          {v.avgLeadTimeDays && <span>· {Number(v.avgLeadTimeDays).toFixed(0)}d lead</span>}
          {v.avgResponseHours && <span>· {Number(v.avgResponseHours).toFixed(0)}h reply</span>}
          {v.lastQuotedAt && (
            <span>· last quote {new Date(v.lastQuotedAt).toLocaleDateString()}</span>
          )}
          {v.preferredCurrency && <span>· {v.preferredCurrency}</span>}
        </div>
      </Link>
    </li>
  );
}
